import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, IconButton, Collapse, Divider } from "@mui/material";
import ReadMoreButton from "../../shared/components/ReadMoreButton";
import { useParams } from "react-router-dom";
import { apiRequest } from "../../shared/api/apiClient";
import LoadingSpinner from "../../shared/components/LoadingSpinner";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import MLSTTable from "../../shared/components/MLSTTable";

export default function AnalyzeResultsMlst() {
    const { id } = useParams();
    const token = process.env.REACT_APP_ATLAS_TOKEN;

    const [mlstData, setMlstData] = useState({}); // objeto { sampleId: [...] o {..} }
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!id || !token) return;

            setLoading(true);

            try {
                const params = new URLSearchParams({
                    study_id: id,
                    token,
                });

                const endpoint = `mlst.php?${params.toString()}`;

                const data = await apiRequest(endpoint, {
                    method: "GET",
                });

                if (!cancelled) {
                    setMlstData(data ?? {});
                }
            } catch (e) {
                if (!cancelled) console.error(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [id, token]);

    return (
        <Box
            sx={{
                width: "100%",
                mx: "auto",
            }}
        >
            {/* Title block */}
            <Box sx={{ width: "100%", mb: 6 }}>
                <Typography variant="h4">MLST</Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                    <Box
                        sx={{
                            width: 14,
                            height: 28,
                            borderTopRightRadius: 999,
                            borderBottomRightRadius: 999,
                            background: "#20BC71",
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant="h5">mlst</Typography>
                </Box>
            </Box>

            {/* Intro + command */}
            <Grid
                container
                spacing={4}
                sx={{
                    width: "100%",
                    mb: 10,
                    display: "flex",
                    justifyContent: "flex-start",
                }}
            >
                {/* Left column */}
                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        <Box sx={{ mb: 5 }}>
                            <Typography variant="body1">These results were generated by MLST.</Typography>
                            <Typography variant="body1">
                                To read the documentation please visit the oficial website
                            </Typography>
                        </Box>

                        <ReadMoreButton href={"https://github.com/tseemann/mlst"} />
                    </Box>
                </Grid>

                {/* Right column */}
                <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                        display: "flex",
                        justifyContent: { xs: "flex-start", md: "flex-end" },
                    }}
                >
                    <Box
                        sx={{
                            background: "#000",
                            color: "#fff",
                            width: "100%",
                            maxWidth: 460,
                            minWidth: { xs: "auto", md: 400 },
                            minHeight: 144,
                            borderRadius: 4,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            p: 2,
                        }}
                    >
                        <Box
                            sx={{
                                textAlign: "left",
                                wordBreak: "break-word",
                                fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                            }}
                        >
                            <Typography variant="body2">{"mlst --input ${spades_scaffold} \\"}</Typography>
                            <Typography variant="body2">{"--outdir mlst/${sample}/"}</Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Loading / Samples */}
            {loading ? (
                <Box
                    sx={{
                        width: "100%",
                        display: "grid",
                        placeItems: "center",
                        mt: 6,
                        gap: 2,
                    }}
                >
                    <LoadingSpinner size={170} barWidth={18} barHeight={48} />
                    <Typography sx={{ fontSize: 40 }}>Processing files</Typography>
                </Box>
            ) : (
                Object.entries(mlstData ?? {}).map(([sampleID, sampleObj]) => (
                    <MlstSample sampleID={sampleID} sampleData={sampleObj} key={sampleID} />
                ))
            )}
        </Box>
    );
}

const MlstSample = ({ sampleID, sampleData }) => {
    const [openSample, setOpenSample] = useState(false);

    // Tu data actual parece ser un array y tú usas sampleData[0]
    const item = Array.isArray(sampleData) ? sampleData[0] : sampleData;

    return (
        <Box sx={{ width: "100%" }}>
            {/* Sample header (clickable like Resfinder) */}
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    cursor: "pointer",
                }}
                onClick={() => setOpenSample((v) => !v)}
            >
                <Box sx={{ width: "100%" }}>
                    <Typography variant="h6">Sample {sampleID}</Typography>
                </Box>

                <Box
                    sx={{
                        width: "10%",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "end",
                    }}
                >
                    <IconButton
                        sx={{
                            maxWidth: '50px',
                            width: "40px",
                            aspectRatio: '1/1',
                            border: "1px solid",
                            borderColor: "divider",
                            flexShrink: 0,
                        }}

                        aria-label={openSample ? "Collapse" : "Expand"}
                    >
                        {openSample ? (
                            <ExpandLessRoundedIcon sx={{ width: "20px", height: "20px" }} />
                        ) : (
                            <ExpandMoreRoundedIcon sx={{ width: "20px", height: "20px" }} />
                        )}
                    </IconButton>
                </Box>
            </Box>

            <Collapse in={openSample} timeout={240} unmountOnExit>
                <Divider sx={{ borderColor: "#20BC71" }} />

                {/* Summary block (igual estilo: ancho fluido + maxWidth) */}
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 760,
                        mt: 3,
                        borderTop: "1px solid #0000001f",
                        borderBottom: "1px solid #0000001f",
                        py: 1,
                    }}
                >
                    <Grid container spacing={2} sx={{ my: 0.5 }}>
                        <Grid item xs={6}>
                            <Typography variant="body2" fontWeight={700}>
                                Sequence type
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2">{item?.sequence_type ?? "—"}</Typography>
                        </Grid>
                    </Grid>

                    <Divider sx={{ borderColor: "#0000001f", my: 1 }} />

                    <Grid container spacing={2} sx={{ my: 0.5 }}>
                        <Grid item xs={6}>
                            <Typography variant="body2" fontWeight={700}>
                                Scheme
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2">{item?.scheme ?? "—"}</Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* Table */}
                <Box sx={{ pb: 3, pt: 2 }}>
                    <MLSTTable alleles={item?.alleles ?? []} />
                </Box>
            </Collapse>

            <Divider sx={{ borderColor: "#20BC71" }} />
        </Box>
    );
};